import {
    USER_SIGNED_IN,
    AuthAction,
    userSignedIn,
    userSignedOut,
    UserSignedIn,
    userTokenUpdated,
    userDocUpdated,
    schoolPermissionsUpdated,
    SIGN_OUT,
    signOut,
    SignOut
} from '../actions/AuthActions';
import { StoreState } from '../types';
import { Epic, combineEpics, ofType } from 'redux-observable';
import { firebaseStore, firebaseAuth, observeChangesDoc, observeChangesAll, firebaseApp, getCurrentProviderId } from '../firebase';
import { createTryActionsEpic } from './Common';
import { Observable, Observer, from } from 'rxjs';
import { switchMap, map, catchError, filter, takeUntil } from 'rxjs/operators';
import { Action } from 'redux';
import { LOCATION_CHANGE, LocationChangeAction, RouterAction, replace } from 'connected-react-router';

const recordUserAuthSessionEpic: Epic<AuthAction, AuthAction, StoreState> =
    createTryActionsEpic(
        USER_SIGNED_IN,
        async (a, s) => {
            const userSignedInAction = a as UserSignedIn;
            if (!userSignedInAction.value) {
                throw new Error('User is null');
            }

            const { uid, displayName, email } = userSignedInAction.value.user;
            const authSessionDoc = await firebaseStore
                .collection('users')
                .doc(uid)
                .collection('authSessions')
                .add({
                    uid,
                    displayName,
                    email,
                    start: new Date()
                });

            return authSessionDoc.id;
        },
        (a, r) => r
    );

const firebaseAuthStateChangedEpic: Epic<AuthAction, AuthAction, StoreState> =
    (action$, store$) =>
        Observable.create((observer: Observer<AuthAction>) => {
            firebaseAuth.onAuthStateChanged(user => {
                observer.next(user ? userSignedIn(user, getCurrentProviderId(user) || user.providerId) : userSignedOut());
            });
        });

const firebaseIdTokenResultEpic: Epic<AuthAction, AuthAction, StoreState> =
    (action$, store$) =>
        action$.pipe(
            ofType<AuthAction, UserSignedIn>(USER_SIGNED_IN),
            switchMap(a => from(a.value.user.getIdTokenResult())),
            map(userTokenUpdated),
            catchError((err, caught) => {
                console.error('Error reading user.getIdTokenResult', err);
                return caught;
            })
        );
 
const userDocEpic: Epic<AuthAction, AuthAction, StoreState> =
    (action$, store$) =>
        action$.pipe(
            ofType<Action, UserSignedIn>(USER_SIGNED_IN),
            switchMap(a => observeChangesDoc(`users/${a.value.user.uid}`).pipe(takeUntil(action$.ofType(SIGN_OUT)))),
            map(userDocUpdated)
        );        

const schoolUserPermissionsEpic: Epic<AuthAction, AuthAction, StoreState> =
    (action$, store$) =>
        action$.pipe(
            ofType<Action, UserSignedIn>(USER_SIGNED_IN),
            switchMap(a => observeChangesAll(`users/${a.value.user.uid}/schools`).pipe(takeUntil(action$.ofType(SIGN_OUT)))),
            map(schoolPermissionsUpdated)
        );

const signoutWhenSignOutPageLoadedEpic: Epic<Action, SignOut, StoreState> =
    (action$, store$) =>
        action$.pipe(
            ofType<Action, LocationChangeAction>(LOCATION_CHANGE),
            filter(lc => lc.payload.location.pathname === '/signout'),
            map(signOut)
        );

const signOutFromFirebaseEpic: Epic<Action, RouterAction, StoreState> = 
    (action$, store$) =>
        action$.pipe(
            ofType<Action, SignOut>(SIGN_OUT),
            switchMap(() => firebaseApp.auth().signOut()),
            map(() => replace('/'))
        );

export default combineEpics(
    recordUserAuthSessionEpic,
    firebaseAuthStateChangedEpic,
    firebaseIdTokenResultEpic,
    userDocEpic,
    schoolUserPermissionsEpic,
    signoutWhenSignOutPageLoadedEpic,
    signOutFromFirebaseEpic
);